import { useFormikContext, getIn } from 'formik';
import { Grid } from '@material-ui/core';
import PropTypes from 'prop-types';
import React, { useCallback, useRef, useState } from 'react';
import throttle from 'lodash-es/throttle';
import * as Sentry from '@sentry/react';

import useSession from 'core/hooks/use-session';
import { Tooltip } from 'core/components/tooltip';
import { TextField } from 'core/components/text-field';
import SmartyStreetsAPI from 'core/helpers/smartyStreetsAPI';
import { Autocomplete } from '../autocomplete';

import useStyles from './address-autocomplete.styles';

const CustomUmbrellaOptionComponent = ({ classes }) => {
  return (
    <div className={classes.customOptionContainer}>
      <span className={classes.customOptionPrefix}>Can't find the address?</span>
      <span className={classes.customOptionCTA}>Add an address not listed in our database</span>
    </div>
  );
};

// Provide 'name' prop to component and add it to switch case statement in onSuggestionSelected...
// ...to correctly autofill the suggested address into corresponding fields
function AddressAutocomplete({ id, name, mode, xs, label, disabled, tooltip, optionsLimit = 5, ...restProps }) {
  const [addressSuggestions, setAddressSuggestions] = useState([]);
  const session = useSession();
  const classes = useStyles();
  const {
    setTouched,
    setValues,
    setFieldTouched,
    setFieldValue,
    values: formValues,
    touched,
    errors
  } = useFormikContext();
  const field = id || name;
  const umbrellaField = field.includes('rental') || field.includes('nonRental');

  const throttledSearch = useRef(
    throttle(async (searchValue) => {
      try {
        const newSuggestions = await SmartyStreetsAPI.search(searchValue, session.allowedStates);

        setAddressSuggestions(
          newSuggestions
            .map((suggestion) => {
              const { streetLine, zipcode, city, state } = suggestion;

              return {
                ...suggestion,
                id: streetLine,
                text: `${streetLine}, ${city}, ${state} ${zipcode}`
              };
            })
            .slice(0, optionsLimit)
        );
      } catch (e) {
        Sentry.captureException(e, { extra: { username: session.user.username } });
      }
    }, 1000)
  );

  const onInputChange = (event) => {
    const {
      target: { value }
    } = event;
    setFieldTouched(id, true, false);
    setFieldValue(id, value);

    if (value) {
      throttledSearch.current(value);
    }
  };

  const onSuggestionSelected = (suggestion) => {
    const suggestedAddress = {
      address: suggestion.streetLine,
      address2: undefined, // ignore suggested secondary line, since it just prefills "Apt" and we want the user to fill it out properly
      city: suggestion.city,
      state: suggestion.state,
      zip: suggestion.zipcode
    };
    const fieldsToBeTouched = { address: true, address2: true, unit: true, city: true, state: true, zip: true };

    if (umbrellaField) {
      if (suggestion.custom) {
        setFieldValue(restProps.propertyType, {
          custom: true,
          displayAddress: 'Add an address not listed in our database',
          address: '',
          address2: '',
          city: '',
          state: '',
          zip: ''
        });
      } else {
        setFieldValue(restProps.propertyType, {
          custom: false,
          address: suggestedAddress.address,
          displayAddress: `${suggestedAddress.address}, ${suggestedAddress.city}, ${suggestedAddress.state} ${suggestedAddress.zip}`,
          address2: suggestedAddress.address2,
          city: suggestedAddress.city,
          state: suggestedAddress.state,
          zip: suggestedAddress.zip
        });

        setFieldTouched(`${restProps.propertyType}.address`, true);
        setFieldTouched(`${restProps.propertyType}.city`, true);
        setFieldTouched(`${restProps.propertyType}.state`, true);
        setFieldTouched(`${restProps.propertyType}.zip`, true);
      }
      return;
    }

    switch (name) {
      case 'primaryMortgageDetail':
        setValues(
          {
            ...formValues,
            primaryMortgageDetail: {
              ...formValues.primaryMortgageDetail,
              mortgageHolderAddress: suggestedAddress
            }
          },
          false
        );
        setTouched(
          {
            ...touched,
            primaryMortgageDetail: {
              mortgageHolderAddress: fieldsToBeTouched
            }
          },
          false
        );
        break;
      case 'mortgageHolderAddress.address':
        setValues({
          ...formValues,
          mortgageHolderAddress: suggestedAddress
        });
        setTouched(
          {
            ...touched,
            mortgageHolderAddress: fieldsToBeTouched
          },
          false
        );
        break;
      case 'rentersLocation':
        setValues({
          ...formValues,
          rentersCoverage: {
            rentersLocation: suggestedAddress
          }
        });
        setTouched({ ...touched, ...fieldsToBeTouched }, false);
        break;
      case 'priorAddress':
        setValues({
          ...formValues,
          priorAddress: { ...suggestedAddress, unit: suggestedAddress.address2 }
        });
        setTouched({ ...touched, priorAddress: { ...fieldsToBeTouched } }, false);
        break;
      case 'mailingAddress':
        setValues({
          ...formValues,
          mailingAddress: suggestedAddress
        });
        setTouched({ ...touched, mailingAddress: { ...fieldsToBeTouched } });
        break;
      case 'address.address':
        setValues({
          ...formValues,
          address: suggestedAddress
        });
        setTouched({ ...touched, address: { ...fieldsToBeTouched } });
        break;
      default:
        if (name?.includes('cars') && name?.includes('garageLocation')) {
          // can't put in its own case statement due to dynamic path
          const carPath = `${name.split('.')[0]}`; // ex: cars[0]
          setFieldValue(`${carPath}.garageLocation`, suggestedAddress);
          setTouched({ ...touched, [carPath]: { garageLocation: { ...fieldsToBeTouched } } }, false);
        } else if (name?.includes('interestedParties')) {
          const additionalPartyPath = `${name.split('.')[0]}`; // ex: interestedParties[0]
          setFieldValue(`${additionalPartyPath}.address`, suggestedAddress);
          setTouched({ ...touched, interestedParties: { ...fieldsToBeTouched } }, false);
        } else if (name?.includes('lienHolders')) {
          const lienHoldersPath = `${name.split('.')[0]}`; // ex: lienholders[0]
          setFieldValue(`${lienHoldersPath}.address`, suggestedAddress);
          setTouched({ ...touched, lienholders: { ...fieldsToBeTouched } }, false);
        } else {
          setValues({
            ...formValues,
            ...suggestedAddress
          });
          setTouched({ ...touched, ...fieldsToBeTouched }, false);
        }
    }
  };

  const onAutocompleteChange = (_, value) => {
    if (value) {
      onSuggestionSelected(value);
    } else {
      setFieldValue(id, null);
    }
  };

  const formValue = getIn(formValues, id) || '';
  const getCurrentValue = useCallback(() => {
    // There once was a bug that caused some policies save with the address formatted like:
    // "address: {city=Columbus, state=OH, zip=43202, address=123 Sesame St.}"
    // The below code accounts for that

    if (typeof formValue === 'string') {
      const value = formValue.includes('{')
        ? formValue.slice(formValue.indexOf('address')).split('=')[1].replace('}', '')
        : formValue;
      return { id: value, text: value };
    }

    return {
      ...formValue,
      text: `${formValue.address}, ${formValue.city}, ${formValue.state}`,
      id: formValue.address
    };
  }, [formValue]);

  const getHelperText = () => {
    return getIn(touched, field) ? getIn(errors, field) : undefined;
  };

  const options = umbrellaField
    ? [
        ...addressSuggestions,
        ...(getIn(touched, id)
          ? [{ custom: true, id: 'custom', text: 'Add an address not listed in our database' }]
          : [])
      ]
    : addressSuggestions;

  return (
    <Grid item xs={xs} className={classes.autocompleteContainer}>
      {tooltip && <Tooltip placement="right" label={tooltip.label || 'More info'} text={tooltip.text} formField />}
      <Autocomplete
        options={options}
        className={classes.noMarginBottom}
        name={field}
        id={id}
        disabled={disabled}
        onSelection={onAutocompleteChange}
        InputComponent={TextField}
        renderOption={(option) => {
          const CustomOptionComponent = umbrellaField ? CustomUmbrellaOptionComponent : () => `custom: ${option.text}`;
          return option.custom ? (
            <CustomOptionComponent classes={classes} />
          ) : (
            <div className={classes.optionText}>{option.text}</div>
          );
        }}
        inputProps={{
          label,
          mode,
          onChange: onInputChange,
          error: getIn(touched, name || id) && !!getIn(errors, name || id),
          helperText: getHelperText()
        }}
        {...restProps}
        value={getCurrentValue()}
      />
    </Grid>
  );
}

AddressAutocomplete.propTypes = {
  id: PropTypes.string.isRequired,
  name: PropTypes.string,
  label: PropTypes.string,
  mode: PropTypes.oneOf(['light', 'dark']).isRequired,
  tooltip: PropTypes.object,
  xs: PropTypes.number,
  disabled: PropTypes.bool
};

AddressAutocomplete.defaultProps = {
  name: undefined,
  label: 'Address',
  tooltip: null
};

export default AddressAutocomplete;
